import {
  Brain,
  Stethoscope,
  ShieldCheck,
  Receipt,
  Calendar,
  FileSignature,
  Sparkles,
  Bell,
  ClipboardCheck,
  Building2,
} from "lucide-react";
import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Card,
  LinkButton,
} from "../components/ui";
import { useSeo } from "../lib/seo";

const FEATURES: Array<{
  icon: any;
  title: string;
  body: string;
  highlights: string[];
}> = [
  {
    icon: Stethoscope,
    title: "Chart ingest",
    body: "Drop charts in any common format. We parse them into resident-level facts and chart snapshots, with full audit trail.",
    highlights: ["PDF · CSV · HL7", "Per-tenant Storage prefix", "Resident UUID hashing on ingest", "Manual + automated source flagging"],
  },
  {
    icon: Brain,
    title: "Clinical engine",
    body: "49 rules across 3 vertical packs — each one anchored to a citation. Evidence comes out of the box.",
    highlights: [
      "SNF: ASCP · F757 / F758 / F881 · Boustani ACB · STOPP/START v2",
      "ASC: AAAHC ch 11 · DEA · high-alert meds · vaccine storage",
      "Dialysis: AAMI water · CMS V-tags · MBD labs · pharmacy oversight",
      "Per-rule severity, NA reasons, suppression resistance",
    ],
  },
  {
    icon: Sparkles,
    title: "AI synthesis",
    body: "Claude Sonnet 4.6 via AWS Bedrock — HIPAA-eligible region, prompt caching, allergy-suppression resistance, cost cap per tenant.",
    highlights: [
      "Per-tenant prompt learning loop",
      "Allergy + drug interaction guardrails",
      "Outcome feedback aggregation",
      "Token budget enforcement",
    ],
  },
  {
    icon: ClipboardCheck,
    title: "Assessment worksheets",
    body: "7 worksheets shipped — ACB anticholinergic burden, STOPP/START v2, Beers 2023, GDR, abx stewardship, Morse fall risk, NRS-11 pain.",
    highlights: [
      "ACB (Boustani 2008)",
      "STOPP/START v2 (O'Mahony 2015)",
      "Beers 2023 (AGS)",
      "CDC Core Elements + F881",
    ],
  },
  {
    icon: FileSignature,
    title: "Bundle render + Adobe Sign",
    body: "SBAR-formatted recommendation bundles signed by medical directors. Embedded experience, HMAC webhook reconciliation.",
    highlights: [
      "React-PDF rendering",
      "Embedded signing widget",
      "Auto status flip on webhook",
      "Append-only audit at every transition",
    ],
  },
  {
    icon: Receipt,
    title: "QuickBooks Online + time tracking",
    body: "Submit hours, auto-draft invoices, link line items. Per-tenant ItemRef. Race-guarded against double-bill.",
    highlights: [
      "OAuth + token refresh",
      "Hourly + bundle line items",
      "Soft-fail safety on zero hours",
      "PHI gate on line item descriptions",
    ],
  },
  {
    icon: Calendar,
    title: "Calendar sync",
    body: "Two-way Google + Outlook sync. License renewal sweep monthly. CMS state filings tracked.",
    highlights: [
      "OAuth Google + Microsoft",
      "Monthly state board sweep",
      "90/30/expired bucketing",
      "Soft-fail tolerance on provider errors",
    ],
  },
  {
    icon: Bell,
    title: "Notifications",
    body: "Resend + Twilio for opt-in reminders. Per-user preferences. Unsubscribe via HMAC tokens. Bounce/delivery webhook ingestion.",
    highlights: [
      "Email + SMS + in-app",
      "8 notification kinds",
      "Per-user channel preferences",
      "CAN-SPAM compliant unsubscribe",
    ],
  },
  {
    icon: ShieldCheck,
    title: "HIPAA + audit",
    body: "Append-only audit log on every action. Vault-stored tenant secrets. 68 PHI test gates locked in CI.",
    highlights: [
      "Append-only audit log",
      "RLS pen-test on every commit",
      "Supabase Vault for tenant secrets",
      "BAA on every covered subprocessor",
    ],
  },
  {
    icon: Building2,
    title: "State board filings",
    body: "Initial + renewal + change-of-info filings. Per-state form schemas. Renewal sweep cron.",
    highlights: [
      "FL AHCA-ASC + GA DCH-ASC (more coming)",
      "PDF render + signed URL distribution",
      "Storage bucket per-tenant prefix",
      "90d / 30d / expired sweep cadence",
    ],
  },
];

export function Features() {
  useSeo({
    title: "Features — WeConsult Rx",
    description:
      "Deep-dive on every WeConsult Rx capability: chart ingest, clinical engine, AI synthesis, assessment worksheets, Adobe Sign bundles, QuickBooks billing, calendar sync, notifications, and state board filings.",
    path: "/features",
  });

  return (
    <>
      <Section className="pb-8">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Features</Eyebrow>
            <Heading level={1} className="mt-3">
              Every capability the consultant pharmacist needs.
            </Heading>
            <Lede className="mt-6">
              We don’t bolt features on. They’re shipped with citations, tests,
              and PHI gates built in from day one.
            </Lede>
          </div>
        </Container>
      </Section>

      <Section className="pt-8">
        <Container>
          <div className="grid gap-6 lg:grid-cols-2">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <Card as="article" key={f.title}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-forest-50 text-forest-700">
                    <Icon className="h-6 w-6" aria-hidden />
                  </div>
                  <Heading level={3} className="mt-5">
                    {f.title}
                  </Heading>
                  <p className="mt-3 text-ink-600">{f.body}</p>
                  <ul className="mt-4 space-y-1.5 text-sm text-ink-700">
                    {f.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2">
                        <span className="text-forest-700">•</span>
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>

          <div className="mt-16 text-center">
            <LinkButton to="/demo" size="lg">
              See it in your workflow
            </LinkButton>
          </div>
        </Container>
      </Section>
    </>
  );
}
